import React, { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { auditApi } from "../../services/auditApi";
import { storeApi } from "../../services/storeApi";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { AuditDetailDrawer } from "../../components/AuditDetailDrawer";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { formatJst } from "../../lib/date";
import { formatAuditAction, formatAuditModule } from "../../lib/auditFormat";
import { profileLabelForRole } from "../../lib/accessProfiles";
import { useLocalization } from "../../hooks/useLocalization";

const PAGE_SIZE = 25;
const MODULES = ["RECRUITMENT", "EMPLOYEES", "ONBOARDING", "SCHEDULING", "TIMEKEEPING", "PAYROLL", "LEAVE", "TRAINING", "COMPLIANCE", "SECURITY", "ADMINISTRATION"];
const EMPTY_FILTERS = { search: "", module: "", result: "", store_location: "", date_from: "", date_to: "" };

export default function AuditLogPage() {
  const { t } = useLocalization();
  const [accessDenied, setAccessDenied] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total_count: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    storeApi.list(false).then((r) => setStores(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => setFilters((f) => ({ ...f, search: searchInput })), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [filters]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    auditApi.list(params)
      .then((r) => setData(r.data))
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); })
      .finally(() => setLoading(false));
  }, [filters, page]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    auditApi.get(selectedId).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [selectedId]);

  const hasActiveFilters = useMemo(() => Object.values(filters).some(Boolean), [filters]);

  if (accessDenied) return <AccessDenied resourceLabel={t("audit.title")} />;

  const from = data.total_count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, data.total_count);

  return (
    <div data-testid="audit-log-page">
      <PageHelpBanner pageKey="audit-log" title={t("pageHelp.defaultTitle")}>
        <p>{t("audit.help")}</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-6">{t("audit.title")}</h2>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="audit-search-input"
            className="pl-9"
            placeholder={t("audit.searchPlaceholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filters.module || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, module: v === "ALL" ? "" : v }))}>
            <SelectTrigger data-testid="audit-filter-module" className="w-44"><SelectValue placeholder={t("audit.allModules")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("audit.allModules")}</SelectItem>
              {MODULES.map((m) => <SelectItem key={m} value={m}>{formatAuditModule(m, t)}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filters.result || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, result: v === "ALL" ? "" : v }))}>
            <SelectTrigger data-testid="audit-filter-result" className="w-36"><SelectValue placeholder={t("audit.allResults")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("audit.allResults")}</SelectItem>
              <SelectItem value="SUCCESS">{t("audit.success")}</SelectItem>
              <SelectItem value="DENIED">{t("audit.denied")}</SelectItem>
            </SelectContent>
          </Select>

          {stores.length > 0 && (
            <Select value={filters.store_location || "ALL"} onValueChange={(v) => setFilters((f) => ({ ...f, store_location: v === "ALL" ? "" : v }))}>
              <SelectTrigger data-testid="audit-filter-store" className="w-44"><SelectValue placeholder={t("audit.store")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t("audit.store")}</SelectItem>
                {stores.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          <Input type="date" data-testid="audit-filter-date-from" className="w-40" value={filters.date_from}
                 onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} title={t("audit.dateFrom")} />
          <Input type="date" data-testid="audit-filter-date-to" className="w-40" value={filters.date_to}
                 onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} title={t("audit.dateTo")} />

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" data-testid="audit-clear-filters" onClick={() => { setFilters(EMPTY_FILTERS); setSearchInput(""); }}>
              <X className="h-3.5 w-3.5 mr-1" />{t("audit.clearFilters")}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <p className="text-sm text-gray-500 py-8 text-center">{t("common.loading")}</p>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center" data-testid="audit-no-results">{t("audit.noResults")}</p>
        ) : (
          <table className="w-full text-sm" data-testid="audit-events-table">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">{t("audit.action")}</th>
                <th className="text-left px-4 py-2">{t("audit.module")}</th>
                <th className="text-left px-4 py-2">{t("audit.actor")}</th>
                <th className="text-left px-4 py-2">{t("audit.employee")}</th>
                <th className="text-left px-4 py-2">{t("audit.result")}</th>
                <th className="text-left px-4 py-2">{t("audit.timestamp")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.items.map((event) => (
                <tr
                  key={event.id}
                  data-testid={`audit-row-${event.id}`}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setSelectedId(event.id)}
                >
                  <td className="px-4 py-2.5 font-medium text-gray-800">{formatAuditAction(event.action, t)}</td>
                  <td className="px-4 py-2.5 text-gray-500">{formatAuditModule(event.module, t)}</td>
                  <td className="px-4 py-2.5 text-gray-600">{event.actor_name || profileLabelForRole(event.actor_role, t)}</td>
                  <td className="px-4 py-2.5 text-gray-600">{event.employee_name || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={event.result === "DENIED" ? "text-red-600 font-medium" : "text-emerald-600"}>
                      {event.result === "DENIED" ? t("audit.denied") : t("audit.success")}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">{formatJst(event.timestamp_jst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data.total_count > 0 && (
        <div className="flex items-center justify-between mt-3 text-sm text-gray-500" data-testid="audit-pagination">
          <span>{t("audit.showingResults", { from, to, total: data.total_count })}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="audit-page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              {t("common.previous")}
            </Button>
            <span data-testid="audit-page-indicator">{t("audit.page", { page, totalPages: data.total_pages })}</span>
            <Button variant="outline" size="sm" data-testid="audit-page-next" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}>
              {t("common.next")}
            </Button>
          </div>
        </div>
      )}

      <AuditDetailDrawer detail={detail} open={!!selectedId} onOpenChange={(open) => { if (!open) setSelectedId(null); }} />
    </div>
  );
}
